"use client"

import { Link } from "react-router-dom"
import { Leaf, Mail, Phone, MapPin, Facebook, Instagram, Send, ArrowRight, Heart, Clock, Shield, ExternalLink } from "lucide-react"
import { useLanguage } from "@/lib/language-context"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"


// Helper function to convert Google Maps URL or iframe code to embed URL
function getGoogleMapsEmbedUrl(input: string): string | null {
  if (!input) return null

  const trimmedInput = input.trim()

  // Check if input is an iframe HTML code - extract src from it
  if (trimmedInput.startsWith('<iframe') || trimmedInput.includes('<iframe')) {
    const srcMatch = trimmedInput.match(/src=["']([^"']+)["']/)
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1]
    }
  }

  // If it's already an embed URL, return as is
  if (trimmedInput.includes('google.com/maps/embed')) {
    return trimmedInput
  }

  // Extract place ID or coordinates from various Google Maps URL formats
  // Format: https://maps.app.goo.gl/xxx or https://goo.gl/maps/xxx
  if (trimmedInput.includes('maps.app.goo.gl') || trimmedInput.includes('goo.gl/maps')) {
    // For short URLs, return null so we show the fallback with link
    return null
  }

  // Format: https://www.google.com/maps/place/...
  if (trimmedInput.includes('google.com/maps/place')) {
    // Extract coordinates if present
    const coordMatch = trimmedInput.match(/@([-\d.]+),([-\d.]+)/)
    if (coordMatch) {
      const lat = coordMatch[1]
      const lng = coordMatch[2]
      return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zCoords!5e0!3m2!1sen!2s!4v1609459200000!5m2!1sen!2s`
    }
  }

  // Format: https://www.google.com/maps?q=...
  if (trimmedInput.includes('google.com/maps') && trimmedInput.includes('q=')) {
    const queryMatch = trimmedInput.match(/[?&]q=([^&]+)/)
    if (queryMatch) {
      const query = queryMatch[1]
      return `https://www.google.com/maps/embed/v1/place?key=&q=${query}`
    }
  }

  // If input contains coordinates directly
  const coordMatch = trimmedInput.match(/([-\d.]+),([-\d.]+)/)
  if (coordMatch) {
    const lat = coordMatch[1]
    const lng = coordMatch[2]
    return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zCoords!5e0!3m2!1sen!2s!4v1609459200000!5m2!1sen!2s`
  }

  return null
}


export function Footer() {
  const { t, siteSettings } = useLanguage()

  const quickLinks = [
    { href: "/", label: t("header", "home") },
    { href: "/products", label: t("header", "products") },
    { href: "/categories", label: t("header", "categories") },
    { href: "/about", label: t("header", "about") },
  ]

  const supportLinks = [
    { href: "/contact", label: t("footer", "contact_us") },
    { href: "/shipping", label: t("footer", "shipping_info") },
    { href: "/returns", label: t("footer", "returns") },
    { href: "/faq", label: t("footer", "faq") },
    { href: "/privacy", label: t("footer", "privacy") },
  ]

  const allSocialLinks = [
    {
      icon: Facebook,
      href: siteSettings?.facebook_url,
      label: "Facebook",
      color: "hover:bg-blue-600",
      bgColor: "bg-blue-600/10 dark:bg-blue-600/20",
    },
    {
      icon: Instagram,
      href: siteSettings?.instagram_url,
      label: "Instagram",
      color: "hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-500/10 to-pink-500/10",
    },
    {
      icon: Send,
      href: siteSettings?.telegram_url,
      label: "Telegram",
      color: "hover:bg-blue-500",
      bgColor: "bg-blue-500/10 dark:bg-blue-500/20",
    },
  ]

  const socialLinks = allSocialLinks.filter(link => link.href && link.href.trim() !== "")


  const mapEmbedUrl = getGoogleMapsEmbedUrl(siteSettings?.location_url || "")

  return (
    <footer className="bg-card border-t relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

      {/* Newsletter Section */}
      <div className="border-b border-border/50">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-bold mb-2">{t("footer", "newsletter_title")}</h3>
              <p className="text-muted-foreground">{t("footer", "newsletter_desc")}</p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
              <Input
                type="email"
                placeholder={t("footer", "newsletter_placeholder")}
                className="flex-1 h-12 rounded-xl"
              />
              <Button className="h-12 px-6 rounded-xl shadow-lg btn-glow">{t("footer", "newsletter_button")}</Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              {siteSettings?.logo_url ? (
                <img
                  src={siteSettings.logo_url || "/placeholder.svg"}
                  alt={siteSettings.site_name || "Logo"}
                  className="h-11 w-auto object-contain"
                />
              ) : (
                <div className="w-11 h-11 bg-primary rounded-xl flex items-center justify-center">
                  <Leaf className="h-6 w-6 text-primary-foreground" />
                </div>
              )}
              <span className="text-2xl font-bold">{siteSettings?.site_name || "TabiatShop"}</span>
            </div>
            <p className="text-muted-foreground mb-6 text-pretty leading-relaxed">
              {t("footer", "description")}
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium">{t("footer", "certified")}</span>
              </div>
              <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium">{t("footer", "delivery_24h")}</span>
              </div>
            </div>

            <div className="flex gap-3">
              {socialLinks.map((social) =>
                social.href && social.href !== "#" ? (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`w-11 h-11 bg-muted rounded-xl flex items-center justify-center hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg ${social.color}`}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ) : null,
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6">{t("footer", "quick_links")}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-all duration-300 flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-primary" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="font-bold text-lg mb-6">{t("footer", "customer_service")}</h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-all duration-300 flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-primary" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-6">{t("footer", "contact")}</h3>
            <ul className="space-y-4">
              {siteSettings?.phone && (
                <li>
                  <a
                    href={`tel:${siteSettings.phone.replace(/\s/g, "")}`}
                    className="flex items-start gap-4 text-muted-foreground hover:text-primary transition-colors group"
                  >
                    <div className="w-11 h-11 bg-muted rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground/70 mb-1">{t("footer", "phone_label")}</p>
                      <p className="font-semibold text-foreground">{siteSettings.phone}</p>
                    </div>
                  </a>
                </li>
              )}
              {siteSettings?.email && (
                <li>
                  <a
                    href={`mailto:${siteSettings.email}`}
                    className="flex items-start gap-4 text-muted-foreground hover:text-primary transition-colors group"
                  >
                    <div className="w-11 h-11 bg-muted rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground/70 mb-1">{t("footer", "email_label")}</p>
                      <p className="font-semibold text-foreground">{siteSettings.email}</p>
                    </div>
                  </a>
                </li>
              )}
              {siteSettings?.address && (
                <li className="flex items-start gap-4 text-muted-foreground">
                  <div className="w-11 h-11 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground/70 mb-1">{t("footer", "address_label")}</p>
                    <p className="font-semibold text-foreground">{siteSettings.address}</p>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>

        {siteSettings?.location_url && (
          <div className="mt-12 pt-12 border-t border-border/50">
            <div className="flex items-center justify-center gap-3 mb-6">
              <h3 className="font-bold text-lg">
                {t("contact", "info_title") || "Bizning joylashuvimiz"}
              </h3>
              <a
                href={siteSettings.location_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                {t("footer", "open_in_maps") || "Xaritada ochish"}
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-border/50 shadow-lg h-[300px] relative">
              {mapEmbedUrl ? (
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location Map"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-muted/50">
                  <MapPin className="w-12 h-12 text-primary mb-4" />
                  <p className="text-muted-foreground mb-4 text-center px-4">
                    {siteSettings.address || "Bizning manzilimiz"}
                  </p>
                  <a
                    href={siteSettings.location_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Google Maps-da ochish
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border/50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              &copy; {new Date().getFullYear()} {siteSettings?.site_name || "TabiatShop"}. {t("footer", "copyright")}
              <Heart className="w-4 h-4 text-red-500 fill-red-500 mx-1" />
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/terms" className="hover:text-primary transition-colors">
                {t("footer", "terms")}
              </Link>
              <Link to="/privacy" className="hover:text-primary transition-colors">
                {t("footer", "privacy")}
              </Link>
            </div>
          </div>

          {/* Developer Credits */}
          <div className="mt-4 pt-4 border-t border-border/30 text-center">
            <p className="text-xs text-muted-foreground/70">
              &copy; {new Date().getFullYear()}{" "}
              <a
                href="https://ibots.uz"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                iBots.Uz
              </a>
              {" && "}
              <a
                href="https://myweb.uz"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                MyWeb.Uz
              </a>
              {" "}All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
